import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Spinner, Alert, Pagination } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faBook, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import SubjectService from '../../services/subject.service';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const Subjects = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchSubjects(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setFilters({ search: value });
  };

  const handleClearFilters = () => {
    setFilters({ search: '' });
  };

  const fetchSubjects = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page };

      // Adicionar filtro de busca
      if (filters.search) params.search = filters.search;

      const data = await SubjectService.getAll(params);
      setSubjects(data.results || []);
      setPagination({
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
        currentPage: page,
        totalPages: Math.ceil((data.count || 0) / 10)
      });
    } catch (err) {
      setError('Erro ao carregar disciplinas. Tente novamente.');
      console.error('Error fetching subjects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchSubjects(page);
  };

  const handleEdit = (subject) => {
    setSelectedSubject(subject);
    setFormData({
      name: subject.name
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedSubject(null);
    setFormData({
      name: ''
    });
  };

  const handleOpenCreateModal = () => {
    setFormData({
      name: ''
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      name: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name
      };
      await SubjectService.partialUpdate(selectedSubject.id, payload);
      await fetchSubjects(pagination.currentPage);
      handleCloseModal();
      showToast('Disciplina atualizada com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating subject:', err);
      showToast('Erro ao atualizar disciplina. Tente novamente.', 'danger');
    }
  };

  const handleCreate = async () => {
    try {
      // Busca a primeira escola ativa do usuário
      const activeSchool = user?.school_users?.find(su => su.active);
      if (!activeSchool) {
        showToast('Nenhuma escola ativa encontrada.', 'warning');
        return;
      }

      const payload = {
        name: formData.name,
        school: activeSchool.school.id
      };
      await SubjectService.create(payload);
      await fetchSubjects(pagination.currentPage);
      handleCloseCreateModal();
      showToast('Disciplina criada com sucesso!', 'success');
    } catch (err) {
      console.error('Error creating subject:', err);
      showToast('Erro ao criar disciplina. Tente novamente.', 'danger');
    }
  };

  const handleDelete = async (subject) => {
    if (!window.confirm(`Tem certeza que deseja excluir a disciplina "${subject.name}"?`)) {
      return;
    }

    try {
      await SubjectService.delete(subject.id);
      await fetchSubjects(pagination.currentPage);
      showToast('Disciplina excluída com sucesso!', 'success');
    } catch (err) {
      console.error('Error deleting subject:', err);
      showToast('Erro ao excluir disciplina. Tente novamente.', 'danger');
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gestão de Disciplinas</h4>
          <p className="mb-0">Visualize e edite as disciplinas da escola</p>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={9}>
              <Form.Group>
                <Form.Label>Buscar Disciplina</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o nome da disciplina..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  onKeyPress={(e) => e.key === 'Enter' && fetchSubjects(1)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faBook} className="me-2" />
                    Lista de Disciplinas
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Nova Disciplina
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && subjects.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhuma disciplina encontrada.</p>
                </div>
              )}
              {!loading && !error && subjects.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Disciplina</th>
                      <th>Escola</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((subject) => (
                      <tr key={subject.id}>
                        <td className="fw-bold">{subject.name}</td>
                        <td>{subject.school_name}</td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(subject)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(subject)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {!loading && !error && subjects.length > 0 && (
              <Card.Footer className="px-3 border-0 d-flex justify-content-center">
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.previous}
                  />
                  {[...Array(pagination.totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pagination.currentPage}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.next}
                  />
                </Pagination>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={handleCloseModal} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Disciplina</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Disciplina</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Matemática"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Disciplina</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Disciplina</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Matemática"
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!formData.name.trim()}
          >
            Criar Disciplina
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Subjects;
